import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Filter, AlertTriangle, CheckCircle2, Info,
  Users, Clock, TrendingDown, Sparkles, BarChart3, Loader2, RefreshCcw,
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../utils/api';

const GENDER_COLORS = { Mujer: '#ec4899', Hombre: '#3b82f6', Desconocido: '#94a3b8' };
const AGE_COLOR = '#6366f1';
const COUNTRY_COLOR = '#10b981';

const SEV_STYLES = {
  critico: { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-900', icon: AlertTriangle, iconColor: 'text-rose-600' },
  alto:    { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', icon: TrendingDown, iconColor: 'text-amber-600' },
  ok:      { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900', icon: CheckCircle2, iconColor: 'text-emerald-600' },
  info:    { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-900', icon: Info, iconColor: 'text-sky-600' },
};

// Escala de azul para heatmap (intensidad por count/max)
const heatColor = (count, max) => {
  if (!count || !max) return 'rgb(248,250,252)'; // slate-50
  const ratio = Math.min(1, count / max);
  // de slate-50 a indigo-700: interpolación
  const r = Math.round(248 - (248 - 67) * ratio);
  const g = Math.round(250 - (250 - 56) * ratio);
  const b = Math.round(252 - (252 - 202) * ratio);
  return `rgb(${r},${g},${b})`;
};

const heatTextColor = (count, max) => {
  if (!count || !max) return 'text-slate-300';
  return count / max > 0.55 ? 'text-white' : 'text-slate-700';
};

const fmtNum = (n) => Number(n || 0).toLocaleString('es-CL');

export default function BIAnalytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [campanaId, setCampanaId] = useState('all');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (campanaId && campanaId !== 'all') params.campana_id = campanaId;
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      const { data: resp } = await api.get('/leads/analytics', { params });
      setData(resp);
    } catch (e) {
      toast.error('Error cargando analítica');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [campanaId, desde, hasta]);

  useEffect(() => { load(); }, [load]);

  const limpiar = () => {
    setCampanaId('all');
    setDesde('');
    setHasta('');
  };

  if (loading && !data) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center" data-testid="bi-analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <p className="text-sm text-slate-500 mt-3">Cargando analítica BI...</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="bi-analytics-root">
      {/* Filtros */}
      <Card className="p-4 border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50/30">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <Filter className="w-4 h-4 text-indigo-500" />
            <span className="text-sm">Filtros BI</span>
          </div>
          <div className="flex flex-col gap-1 min-w-[220px]">
            <Label className="text-xs text-slate-500">Campaña</Label>
            <Select value={campanaId} onValueChange={setCampanaId}>
              <SelectTrigger className="h-9 bg-white" data-testid="bi-campana-select">
                <SelectValue placeholder="Todas las campañas" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Todas las campañas</SelectItem>
                {(data?.campanas_disponibles || []).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-500">Desde</Label>
            <Input
              type="date"
              value={desde}
              onChange={e => setDesde(e.target.value)}
              className="h-9 bg-white w-[150px]"
              data-testid="bi-fecha-desde"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-500">Hasta</Label>
            <Input
              type="date"
              value={hasta}
              onChange={e => setHasta(e.target.value)}
              className="h-9 bg-white w-[150px]"
              data-testid="bi-fecha-hasta"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={limpiar}
            data-testid="bi-filtros-limpiar"
            disabled={campanaId === 'all' && !desde && !hasta}
          >
            Limpiar
          </Button>
          <Button
            size="sm"
            onClick={load}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="bi-refresh-btn"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Aplicar
          </Button>
        </div>
      </Card>

      {/* Embudo + Insights */}
      <FunnelSection funnel={data?.funnel} />

      {/* Demografía Meta */}
      <DemographicsSection demograficos={data?.demograficos} />

      {/* Heatmap */}
      <HeatmapSection heatmap={data?.heatmap} />
    </div>
  );
}

// ============================================================
// FUNNEL
// ============================================================

const FunnelSection = ({ funnel }) => {
  if (!funnel) return null;
  const pasos = funnel.pasos || [];
  const maxC = Math.max(...pasos.map(p => p.count), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Embudo visual */}
      <Card className="p-5 lg:col-span-2 border-slate-200" data-testid="bi-funnel-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-500" /> Embudo de Conversión
          </h3>
          <Badge variant="outline" className="text-xs">{funnel.total_leads} leads</Badge>
        </div>
        {funnel.total_leads === 0 ? (
          <p className="text-sm text-slate-500 italic py-6 text-center">Sin leads en el rango seleccionado.</p>
        ) : (
          <div className="space-y-3">
            {pasos.map((p, i) => {
              const w = Math.max(8, Math.round((p.count / maxC) * 100));
              const color = p.etapa === 'Aceptado'
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                : i === 0
                  ? 'bg-gradient-to-r from-slate-400 to-slate-500'
                  : 'bg-gradient-to-r from-indigo-500 to-violet-500';
              return (
                <div key={p.etapa} data-testid={`funnel-paso-${p.etapa.replace(/\s+/g, '-')}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700">{p.etapa}</span>
                    <span className="text-slate-500 tabular-nums">
                      {fmtNum(p.count)} · {p.pct_total}% total
                      {i > 0 && <span className="ml-2 text-indigo-600 font-semibold">{p.pct_step}% del paso</span>}
                    </span>
                  </div>
                  <div className="relative h-7 bg-slate-100 rounded-md overflow-hidden">
                    <div
                      className={`h-full ${color} flex items-center justify-end pr-2 text-[11px] text-white font-semibold transition-all duration-500`}
                      style={{ width: `${w}%` }}
                    >
                      {w > 15 && <span>{p.count}</span>}
                    </div>
                  </div>
                  {i > 0 && p.drop_pct_step > 0 && (
                    <p className="text-[10px] text-rose-600 mt-0.5 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" /> Caída {p.drop_pct_step}% vs paso anterior
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Insights prescriptivos */}
      <Card className="p-5 border-slate-200" data-testid="bi-insights-card">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-500" /> Insights prescriptivos
        </h3>
        {(funnel.insights || []).length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-xs italic">
            No hay alertas críticas con los filtros actuales.
          </div>
        ) : (
          <div className="space-y-2">
            {funnel.insights.map((ins, idx) => {
              const s = SEV_STYLES[ins.severidad] || SEV_STYLES.info;
              const Icon = s.icon;
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${s.bg} ${s.border}`}
                  data-testid={`bi-insight-${ins.severidad}-${idx}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold ${s.text}`}>{ins.titulo}</p>
                      <p className={`text-[11px] mt-0.5 ${s.text} opacity-90 leading-snug`}>{ins.detalle}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

// ============================================================
// DEMOGRAFICOS (Meta Ads)
// ============================================================

const DemographicsSection = ({ demograficos }) => {
  if (!demograficos) return null;

  if (!demograficos.disponible) {
    return (
      <Card className="p-5 border-amber-200 bg-amber-50" data-testid="bi-demo-unavailable">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900">Demografía Meta no disponible</h3>
            <p className="text-xs text-amber-700 mt-1">
              {demograficos.razon_no_disponible || 'Configura META_SYSTEM_USER_TOKEN y META_AD_ACCOUNT_ID para ver datos demográficos.'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 border-slate-200" data-testid="bi-demograficos-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-500" /> Demografía de la audiencia
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Fuente: {demograficos.fuente} · {fmtNum(demograficos.total_impressions)} impresiones · {fmtNum(demograficos.total_leads)} leads atribuidos
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Género - PieChart */}
        <div data-testid="bi-demo-genero">
          <p className="text-xs font-semibold text-slate-600 mb-2">Por Género</p>
          {(demograficos.por_genero || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={demograficos.por_genero}
                  dataKey="impressions"
                  nameKey="genero"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ genero, percent }) => `${genero}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize={11}
                >
                  {demograficos.por_genero.map((entry, idx) => (
                    <Cell key={idx} fill={GENDER_COLORS[entry.genero] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtNum(v) + ' imp.'} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Edad - BarChart */}
        <div data-testid="bi-demo-edad">
          <p className="text-xs font-semibold text-slate-600 mb-2">Por Rango de Edad</p>
          {(demograficos.por_edad || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={demograficos.por_edad} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <XAxis dataKey="edad" fontSize={11} stroke="#64748b" />
                <YAxis fontSize={10} stroke="#94a3b8" width={50} />
                <Tooltip formatter={(v) => fmtNum(v) + ' imp.'} />
                <Bar dataKey="impressions" fill={AGE_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Región (Chile) - BarChart horizontal */}
        <div data-testid="bi-demo-region">
          <p className="text-xs font-semibold text-slate-600 mb-2">Top Regiones / Ciudades</p>
          {(demograficos.por_region || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, (demograficos.por_region.length * 22) + 30)}>
              <BarChart
                data={demograficos.por_region}
                layout="vertical"
                margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
              >
                <XAxis type="number" fontSize={10} stroke="#94a3b8" />
                <YAxis dataKey="region" type="category" fontSize={10} stroke="#64748b" width={130} interval={0} />
                <Tooltip formatter={(v) => fmtNum(v) + ' imp.'} />
                <Bar dataKey="impressions" fill={COUNTRY_COLOR} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Card>
  );
};

// ============================================================
// HEATMAP día × hora
// ============================================================

const HeatmapSection = ({ heatmap }) => {
  const dias = heatmap?.dias || [];
  const cells = heatmap?.cells || [];

  // matriz [dia][hora] = count
  const matrix = useMemo(() => {
    const m = {};
    for (const d of dias) m[d] = {};
    for (const c of cells) {
      if (!m[c.dia]) m[c.dia] = {};
      m[c.dia][c.hora] = c.count;
    }
    return m;
  }, [cells, dias]);

  // Mejor hora & día
  const best = useMemo(() => {
    let bestCell = null;
    for (const c of cells) {
      if (!bestCell || c.count > bestCell.count) bestCell = c;
    }
    return bestCell;
  }, [cells]);

  if (!heatmap) return null;
  const { horas, max } = heatmap;

  return (
    <Card className="p-5 border-slate-200" data-testid="bi-heatmap-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-500" /> Mapa de calor temporal (Día × Hora)
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Captura de leads según hora de Chile. Intensidad relativa al pico (máximo: {max}).
          </p>
        </div>
        {best && best.count > 0 && (
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300 text-[11px]">
            Pico: {best.dia} {String(best.hora).padStart(2, '0')}:00 · {best.count} leads
          </Badge>
        )}
      </div>

      {max === 0 ? (
        <p className="text-sm text-slate-400 italic py-6 text-center">Sin datos para los filtros actuales.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-[11px] border-separate border-spacing-0.5" data-testid="bi-heatmap-table">
            <thead>
              <tr>
                <th className="w-10"></th>
                {horas.map(h => (
                  <th key={h} className="font-normal text-slate-500 px-1 tabular-nums">
                    {String(h).padStart(2, '0')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dias.map(d => (
                <tr key={d}>
                  <td className="font-semibold text-slate-600 pr-1">{d}</td>
                  {horas.map(h => {
                    const v = matrix[d][h] || 0;
                    return (
                      <td
                        key={h}
                        className={`min-w-[28px] h-7 text-center font-medium rounded ${heatTextColor(v, max)}`}
                        style={{ backgroundColor: heatColor(v, max) }}
                        title={`${d} ${String(h).padStart(2, '0')}:00 - ${v} lead${v !== 1 ? 's' : ''}`}
                        data-testid={`heatmap-cell-${d}-${h}`}
                      >
                        {v > 0 ? v : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {/* Leyenda */}
          <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-500">
            <span>Menos</span>
            <div className="flex gap-0.5">
              {[0, 0.2, 0.4, 0.6, 0.8, 1].map(r => (
                <div key={r} className="w-5 h-3 rounded" style={{ backgroundColor: heatColor(r * max, max) }} />
              ))}
            </div>
            <span>Más</span>
          </div>
        </div>
      )}
    </Card>
  );
};
